import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const API = import.meta.env.VITE_API_URL;

interface HealthHistory {
  id: string;
  created_at: string;
  battery_level?: number;
  charging?: number;
  queue_size?: number;
  uploaded_messages?: number;
  failed_uploads?: number;
  sync_state?: string;
}

export default function DiagnosticsPage(): React.ReactElement {
  const { deviceId } = useParams<{ deviceId: string }>();
  const navigate = useNavigate();
  const [history, setHistory] = useState<HealthHistory[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchHealth = async (): Promise<void> => {
      try {
        const token = localStorage.getItem('velinko_token');
        if (!token) return navigate('/login');

        const res = await fetch(`${API}/devices/${deviceId}/health`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (res.ok) {
          const { data } = await res.json();
          setHistory(data.healthHistory || []);
        }
      } catch (err) {
        console.error('Failed to fetch diagnostics', err);
      } finally {
        setLoading(false);
      }
    };

    fetchHealth();
    const interval = setInterval(fetchHealth, 10000);
    return () => clearInterval(interval);
  }, [deviceId, navigate]);

  if (loading) {
    return <div style={{ padding: '2rem', color: 'var(--text-primary)', backgroundColor: 'var(--bg-primary)', height: '100vh' }}>Loading diagnostics...</div>;
  }

  return (
    <div style={{ padding: '2rem', backgroundColor: 'var(--bg-primary)', color: 'var(--text-primary)', minHeight: '100vh', fontFamily: 'system-ui' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <h1>Device Diagnostics</h1>
        <button 
          onClick={() => navigate('/dashboard')}
          style={{ padding: '0.5rem 1rem', background: 'var(--border-color)', color: 'var(--text-primary)', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
        >
          Back to Dashboard
        </button>
      </div>

      <div style={{ backgroundColor: 'var(--bg-surface)', borderRadius: '8px', padding: '1rem', overflowX: 'auto', border: '1px solid var(--border-color)' }}>
        <table style={{ width: '100%', textAlign: 'left', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
              <th style={{ padding: '0.75rem' }}>Timestamp</th>
              <th style={{ padding: '0.75rem' }}>Battery</th>
              <th style={{ padding: '0.75rem' }}>Queue Size</th>
              <th style={{ padding: '0.75rem' }}>Uploaded</th>
              <th style={{ padding: '0.75rem' }}>Failed</th>
              <th style={{ padding: '0.75rem' }}>State</th>
            </tr>
          </thead>
          <tbody>
            {history.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-secondary)' }}>No diagnostics data available</td>
              </tr>
            ) : (
              history.map((h, i) => (
                <tr key={h.id || i} style={{ borderBottom: '1px solid var(--bg-surface-hover)' }}>
                  <td style={{ padding: '0.75rem' }}>{new Date(h.created_at).toLocaleString()}</td>
                  <td style={{ padding: '0.75rem' }}>
                    {h.battery_level !== undefined && h.battery_level !== null ? `${h.battery_level}% ` : 'N/A '}
                    {h.charging ? '⚡' : ''}
                  </td>
                  <td style={{ padding: '0.75rem', color: h.queue_size && h.queue_size > 10 ? 'var(--status-error)' : 'var(--text-primary)' }}>
                    {h.queue_size ?? 0}
                  </td>
                  <td style={{ padding: '0.75rem' }}>{h.uploaded_messages ?? 0}</td>
                  <td style={{ padding: '0.75rem', color: h.failed_uploads && h.failed_uploads > 0 ? 'var(--status-error)' : 'var(--text-primary)' }}>
                    {h.failed_uploads ?? 0}
                  </td>
                  <td style={{ padding: '0.75rem' }}>{h.sync_state || 'IDLE'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
